import React, { useCallback } from "react";
import { TableRow, TableCell, Checkbox, Tooltip } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { confirmAlert } from "react-confirm-alert";
import clsx from "clsx";
import ElipsisCell from "../../../DesignSystem/Table/Cells/ElipsisTextCell";
import Button from "../../../DesignSystem/button";
import CheckboxIcon from "../../../../assets/images/checkbox-unchecked.svg";
import CheckboxCheckedIcon from "../../../../assets/images/checkbox-checked.svg";
import SoldIcon from "../../../../assets/images/sold-icon.svg";
import EditIcon from "../../../../assets/images/edit-icon.svg";
import InUseIcon from "../../../../assets/images/in-use-icon.svg";
import WarningInfoIcon from "../../../../assets/images/warn-toast-icon.svg";
import { areYouSureToaster, getDateInMediumFormat } from "../../../../utils/methods";
import AuthService from "../../../../services/auth.service";
import { Roles } from "../../../../enums";
import { HeadCellType } from "../../../../utils/appConstants";
import { PortDetails } from "../../../PortDetails";
import { StatusButton } from "../StatusButton";
import MarketplaceButton from "../MarketplaceButton";
import WaveXchangeButton from "../WaveXchangeButton";

interface GroupsPortRowProps {
  port: any;
  rowSelected: boolean;
  selected: any[];
  groupId: string;
  selectedUnGroupId: string;
  removeLoadingId: string;
  cancelSubLoading: string;
  on_net: boolean;
  onSelectRow: (newSelected: any[]) => void;
  onSaleClick: (port: any) => void;
  onSaleEdit: (port: any) => void;
  onRemoveClick: (port: any) => void;
  onCancelSubscription: (port: any) => void;
  handleEditPort: (port: any) => void;
  onRequestVerification: (port: any) => void;
  refetch: () => void;
}

const GroupsPortRow: React.FC<GroupsPortRowProps> = ({
  port,
  groupId,
  on_net,
  onRequestVerification,
  handleEditPort: onEditPort,
  selectedUnGroupId,
  rowSelected,
  selected,
  cancelSubLoading,
  removeLoadingId,
  onCancelSubscription,
  onSelectRow,
  onSaleClick,
  onSaleEdit,
  onRemoveClick,
  refetch,
}) => {
  const isAdmin = AuthService.getRole() === Roles.admin;
  const handleSale = useCallback(() => {
    onSaleClick(port);
  }, [onSaleClick, port]);

  const handleEdit = useCallback(() => {
    onSaleEdit(port);
  }, [onSaleEdit, port]);

  const handleRemove = useCallback(() => {
    return areYouSureToaster(onRemoveClick, port, "Are you sure you want to remove this listing?");
  }, [onRemoveClick, port]);

  const handleEditPort = useCallback(() => {
    onEditPort(port);
  }, [port, onEditPort]);

  const handleCancel = useCallback(() => {
    return areYouSureToaster(onCancelSubscription, port, "Are you sure to you want to cancel this subscription?");
  }, [port, onCancelSubscription]);

  const handleSelectRow = useCallback(() => {
    const selectedIndex = selected.findIndex((item) => item.nrId == port.nr_id || item.purchasePortId == port.purchasePortId);
    let newSelected: any = [];

    if (groupId !== selectedUnGroupId) {
      newSelected = [{ nrId: port.nr_id || -1, purchasePortId: port.purchasePortId || -1 }];
    } else if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, [{ nrId: port.nr_id || -1, purchasePortId: port.purchasePortId || -1 }]);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }

    onSelectRow(newSelected);
  }, [port.nr_id, port.purchasePortId, selected, onSelectRow, groupId, selectedUnGroupId]);

  const handleRequestVerification = useCallback(() => {
    onRequestVerification(port);
  }, [onRequestVerification, port]);

  return (
    <TableRow className="group-inner-body">
      <TableCell align="left" className="group-inner-row-1">
        <Checkbox
          classes={{
            root: "selection-checkbox",
          }}
          color="default"
          checked={rowSelected}
          onChange={handleSelectRow}
          icon={<img src={CheckboxIcon} alt="Checkbox" className="checkbox-icon" />}
          checkedIcon={<img src={CheckboxCheckedIcon} alt="Checkbox Checked" className="checkbox-checked-icon" />}
          inputProps={{
            // @ts-ignore
            "data-testid": "checkbox-input",
          }}
          data-testid="checkbox"
        />
      </TableCell>
      <TableCell align="left" className="group-inner-row-2">
        {`${port.port_name || ""} `}
        <PortDetails nrId={port.nr_id} refreshIndex={refetch} />
      </TableCell>
      <TableCell className="group-inner-row-3">{port.organization_name || "-"}</TableCell>
      {/* <TableCell align="left" align="left">{port.service_description || '-'}</TableCell> */}
      <TableCell align="left" className="group-inner-row-4">
        <span className="group-port-manage-actions">
          <StatusButton port={port} size={40} />
        </span>
      </TableCell>
      <TableCell align="left" className="group-inner-row-5">
        <span className="group-port-manage-actions">
          <MarketplaceButton
            port={port}
            handleSale={handleSale}
            removeLoadingId={removeLoadingId}
            isAdmin={isAdmin}
            cancelSubLoading={cancelSubLoading}
            handleRemove={handleRemove}
            handleEdit={handleEdit}
            handleCancel={handleCancel}
          />
        </span>
      </TableCell>
      <TableCell align="left" className="group-inner-row-6">
        <WaveXchangeButton port={port} on_net={on_net} handleRequestVerification={handleRequestVerification} refresh={refetch} />
      </TableCell>
    </TableRow>
  );
};

export default GroupsPortRow;
